import { useState, useEffect, useCallback } from "react";
import { useUserId } from "./useUserId";
import { useClientPrincipal } from "../context/ClientPrincipalContext";
import { useNotification } from "../context/NotificationContext";
import { validateUrl } from "../components/utils/urlHelpers";
import { LinkTree, LinkItem } from "../types/linkTree.types";

interface ValidationResult {
  ok: boolean;
  value?: string;
  error?: string;
}

interface UseLinkTreeReturn {
  linkTree: LinkTree | null;
  items: LinkItem[];
  loading: boolean;
  error: string | null;

  isAddingLink: boolean;
  editingItem: LinkItem | null;
  isEditingName: boolean;
  newLinkTitle: string;
  newLinkUrl: string;
  linkTreeName: string;

  showQR: boolean;
  qrCodeUrl: string;

  setIsAddingLink: (value: boolean) => void;
  setEditingItem: (item: LinkItem | null) => void;
  setIsEditingName: (value: boolean) => void;
  setNewLinkTitle: (value: string) => void;
  setNewLinkUrl: (value: string) => void;
  setLinkTreeName: (value: string) => void;
  setShowQR: (value: boolean) => void;

  handleAddLink: () => Promise<void>;
  handleUpdateLink: (itemId: string) => Promise<void>;
  handleDeleteLink: (itemId: string) => Promise<void>;
  handleUpdateName: () => Promise<void>;
  generateQRCode: (url: string) => void;
  copyToClipboard: (text: string) => Promise<void>;
  copyLinkHubLink: () => Promise<void>;
  refreshData: () => Promise<void>;
  getShareLink: (shortCode?: string) => string;
}

export const useLinkTree = (): UseLinkTreeReturn => {
  const userId = useUserId();
  const { clientPrincipal } = useClientPrincipal();
  const { setNotification } = useNotification();

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "https://api.shortfy.it";
  const frontendUrl =
    process.env.REACT_APP_FRONTEND_URL || window.location.origin;

  const getShareLink = (shortCode?: string) => {
    if (!shortCode) return `${frontendUrl}/CGI-`;
    return `${frontendUrl}/CGI-${shortCode}`;
  };
  const [linkTree, setLinkTree] = useState<LinkTree | null>(null);
  const [items, setItems] = useState<LinkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isAddingLink, setIsAddingLink] = useState(false);
  const [editingItem, setEditingItem] = useState<LinkItem | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newLinkTitle, setNewLinkTitle] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [linkTreeName, setLinkTreeName] = useState("");

  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const getHeaders = useCallback(
    (includeContentType = false): Record<string, string> => {
      const headers: Record<string, string> = {};

      if (userId) {
        headers["client-principal-user-id"] = userId;
      }

      if (clientPrincipal?.userName) {
        headers["client-principal-user-name"] = clientPrincipal.userName;
      }

      if (includeContentType) {
        headers["Content-Type"] = "application/json";
      }

      return headers;
    },
    [userId, clientPrincipal],
  );

  const fetchLinkTree = useCallback(async (): Promise<void> => {
    if (!userId) return;

    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/linktree`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setLinkTree(data.linkTree);
        setItems(data.items);
        setLinkTreeName(data.linkTree?.name || "My LinkTree");
      } else {
        throw new Error("Failed to fetch LinkTree");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userId, getHeaders, backendUrl]);

  useEffect(() => {
    if (userId) {
      fetchLinkTree();
    }
  }, [fetchLinkTree, userId]);

  const handleAddLink = async (): Promise<void> => {
    if (!newLinkTitle.trim() || !newLinkUrl.trim()) {
      setNotification({
        type: "error",
        message: "Please fill in both title and URL",
      });
      return;
    }

    const urlValidation: ValidationResult = validateUrl(newLinkUrl);
    if (!urlValidation.ok) {
      setNotification({
        type: "error",
        message: urlValidation.error || "Invalid URL",
      });
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/linktree/item`, {
        method: "POST",
        headers: getHeaders(true),
        body: JSON.stringify({
          title: newLinkTitle.trim(),
          url: urlValidation.value,
        }),
      });

      if (response.ok) {
        const result = await response.json();

        if (result.shortCode) {
          setNotification({
            type: "success",
            message: "LinkTree created! Your share link is ready.",
          });
        } else {
          setNotification({
            type: "success",
            message: "Link added successfully!",
          });
        }

        await fetchLinkTree();
        setNewLinkTitle("");
        setNewLinkUrl("");
        setIsAddingLink(false);
      } else {
        const errorData = await response.json();
        setNotification({
          type: "error",
          message: errorData.error || "Failed to add link",
        });
      }
    } catch (err) {
      setNotification({
        type: "error",
        message: "Failed to add link",
      });
    }
  };

  const handleUpdateLink = async (itemId: string): Promise<void> => {
    if (!editingItem) return;

    if (!editingItem.title?.trim() || !editingItem.url?.trim()) {
      setNotification({
        type: "error",
        message: "Please fill in both title and URL",
      });
      return;
    }

    const urlValidation: ValidationResult = validateUrl(editingItem.url);
    if (!urlValidation.ok) {
      setNotification({
        type: "error",
        message: urlValidation.error || "Invalid URL",
      });
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/linktree/item/${itemId}`, {
        method: "PUT",
        headers: getHeaders(true),
        body: JSON.stringify({
          title: editingItem.title.trim(),
          url: urlValidation.value,
        }),
      });

      if (response.ok) {
        await fetchLinkTree();
        setEditingItem(null);
        setNotification({
          type: "success",
          message: "Link updated successfully",
        });
      } else {
        const errorData = await response.json();
        setNotification({
          type: "error",
          message: errorData.error || "Failed to update link",
        });
      }
    } catch (err) {
      setNotification({
        type: "error",
        message: "Failed to update link",
      });
    }
  };

  const handleDeleteLink = async (itemId: string): Promise<void> => {
    try {
      const response = await fetch(`${backendUrl}/linktree/item/${itemId}`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      if (response.ok) {
        await fetchLinkTree();
        setNotification({
          type: "success",
          message: "Link deleted successfully",
        });
      } else {
        setNotification({
          type: "error",
          message: "Failed to delete link",
        });
      }
    } catch (err) {
      setNotification({
        type: "error",
        message: "Failed to delete link",
      });
    }
  };

  const handleUpdateName = async (): Promise<void> => {
    if (!linkTreeName.trim()) {
      setNotification({
        type: "error",
        message: "Please enter a name",
      });
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/linktree/name`, {
        method: "PUT",
        headers: getHeaders(true),
        body: JSON.stringify({
          name: linkTreeName.trim(),
        }),
      });

      if (response.ok) {
        await fetchLinkTree();
        setIsEditingName(false);
        setNotification({
          type: "success",
          message: "Name updated successfully",
        });
      } else {
        setNotification({
          type: "error",
          message: "Failed to update name",
        });
      }
    } catch (err) {
      setNotification({
        type: "error",
        message: "Failed to update name",
      });
    }
  };

  const generateQRCode = (url: string): void => {
    setQrCodeUrl(
      `data:image/png;base64,placeholder-qr-code-for-${encodeURIComponent(url)}`,
    );
    setShowQR(true);
  };

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setNotification({
        type: "success",
        message: "Copied to clipboard!",
      });
    } catch (err) {
      setNotification({
        type: "error",
        message: "Failed to copy to clipboard",
      });
    }
  };

  const copyLinkHubLink = async (): Promise<void> => {
    const shortUrl = getShareLink(linkTree?.shortCode);
    const displayText = linkTree?.name
      ? `${linkTree.name}'s LinkHub`
      : "My LinkHub";

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const htmlBlob = new Blob(
          [`<a href="${shortUrl}">${displayText}</a>`],
          { type: "text/html" },
        );
        const plainBlob = new Blob([`${displayText}: ${shortUrl}`], {
          type: "text/plain",
        });

        const clipboardItem = new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": plainBlob,
        });

        await navigator.clipboard.write([clipboardItem]);
        setNotification({
          type: "success",
          message: "Link copied to clipboard!",
        });
      } else {
        await navigator.clipboard.writeText(`${displayText}: ${shortUrl}`);
        setNotification({
          type: "success",
          message: "Link copied to clipboard!",
        });
      }
    } catch (err) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = `${displayText}: ${shortUrl}`;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        textArea.style.pointerEvents = "none";
        document.body.appendChild(textArea);
        textArea.select();
        const success = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (success) {
          setNotification({
            type: "success",
            message: "Link copied to clipboard!",
          });
        } else {
          throw new Error("Copy failed");
        }
      } catch (fallbackErr) {
        setNotification({
          type: "error",
          message: "Failed to copy to clipboard",
        });
      }
    }
  };

  return {
    linkTree,
    items,
    loading,
    error,

    isAddingLink,
    editingItem,
    isEditingName,
    newLinkTitle,
    newLinkUrl,
    linkTreeName,

    showQR,
    qrCodeUrl,

    setIsAddingLink,
    setEditingItem,
    setIsEditingName,
    setNewLinkTitle,
    setNewLinkUrl,
    setLinkTreeName,
    setShowQR,

    handleAddLink,
    handleUpdateLink,
    handleDeleteLink,
    handleUpdateName,
    generateQRCode,
    copyToClipboard,
    copyLinkHubLink,
    refreshData: fetchLinkTree,
    getShareLink,
  };
};
